import equal from "fast-deep-equal";

import { fromDotObject } from "../../../utils/dotObject";
import mergeDocumentData from "../../../utils/mergeDocumentData";

import { channelAcceptsArticle } from "../../distribution/utils/deliverArticlesToChannels";

import articleDeliveryStatuses from "../constants/articleDeliveryStatuses";
import articleDeliveryTypes from "../constants/articleDeliveryTypes";
import articleStatuses from "../constants/articleStatuses";
import getArticleChannelData from "../getArticleChannelData";
import { ArticleChannelMetaData } from "../types/ArticleChannel";
import { ArticleMetaData } from "../types/ArticleMetaData";

const staleArticleDeliveryStatuses = [
	articleDeliveryStatuses.DELIVERY_UNSUPPORTED,
	articleDeliveryStatuses.DELIVERY_NEEDED,
];

export default async function updateArticleChannelDeliveryNeeded(firebase, { article, data, transaction }) {
	// A channel without an entry would only get a status written, leaving an entry with no channel data
	const channelIds = (data.channelIds || article.channelIds || []).filter(
		(channelId) => article.channels?.[channelId],
	);
	const deliveryTypes = Object.values(articleDeliveryTypes);

	const metaDataBefore = ArticleMetaData.partial().safeParse(article).data || article;
	const metaDataAfter = data ? ArticleMetaData.partial().parse(data) : {};

	const updatedMetaDataKeys = Object.keys(metaDataAfter).filter((key) =>
		isKeyUpdated(key, metaDataBefore[key], metaDataAfter[key]),
	);

	const channelRefs = Object.keys(article.channels || {}).map((id) =>
		firebase.firestore().collection("distributionChannels").doc(id),
	);

	const channels = await transaction.getAllDocuments(channelRefs);

	const mergedArticle = mergeDocumentData(article, data);

	const channelsWithDeliveryTypes = channelIds.map((channelId) => {
		return [
			channelId,
			deliveryTypes.filter((deliveryType) => {
				const channel = channels.find((channel) => channel.id === channelId);
				const { status } = article.channels?.[channelId]?.delivery?.[deliveryType] || {};

				if (data.channels?.[channelId]?.delivery?.[deliveryType]?.status) {
					return false;
				}

				if (staleArticleDeliveryStatuses.includes(status)) {
					return false;
				}

				if (channel && !channelAcceptsArticle({ article: mergedArticle, channel, deliveryType })) {
					return false;
				}

				const updatedChannelMetaDataKeys = Object.keys(ArticleChannelMetaData.shape).filter((key) => {
					const before = getArticleChannelData(article, channelId, key);
					const after = getArticleChannelData(mergedArticle, channelId, key);
					return isKeyUpdated(key, before, after);
				});

				if (deliveryType === articleDeliveryTypes.METADATA) {
					if (channel?.forceDeliveryOfMetadataAndFilesSimultaneously && !article?.artifact?.ref) {
						if (data.artifact) {
							return true;
						}

						return false;
					}

					if (data.status === articleStatuses.READY && article.status !== articleStatuses.READY) {
						return true;
					}

					if (updatedChannelMetaDataKeys.filter(filterDisabledKeys(channel)).length > 0) {
						return true;
					}

					const enabledMetaDataKeys = updatedMetaDataKeys.filter(filterDisabledKeys(channel));

					if (enabledMetaDataKeys.length > 0) {
						return enabledMetaDataKeys.some((key) => {
							return !(article?.channels?.[channelId]?.[key]?.length > 0);
						});
					}
				}

				if (deliveryType === articleDeliveryTypes.ARTIFACT) {
					if (data.artifact) {
						return true;
					}

					if (!article?.artifact?.ref) {
						// if no files exists, return false.
						return false;
					}

					if (data.status === articleStatuses.READY && article.status !== articleStatuses.READY) {
						return true;
					}

					if (
						updatedChannelMetaDataKeys.includes("releaseDate") &&
						status !== articleDeliveryStatuses.DELIVERY_COMPLETED
					) {
						return true;
					}
				}

				return false;
			}),
		];
	});

	return channelsWithDeliveryTypes.reduce((data, [channelId, deliveryTypes]) => {
		return deliveryTypes.reduce((data, deliveryType) => {
			return fromDotObject({
				...data,
				[`channels.${channelId}.delivery.${deliveryType}.status`]: articleDeliveryStatuses.DELIVERY_NEEDED,
			});
		}, data);
	}, data);
}

export function isKeyUpdated(key, before, after) {
	if (isNotAValue(before) && isNotAValue(after)) {
		return false;
	}

	if (key === "cover") {
		return before?.md5Hash !== after?.md5Hash;
	}

	return !equal(before, after);
}

function isNotAValue(value) {
	return value === undefined || value === null || (Array.isArray(value) && value.length === 0);
}

function filterDisabledKeys(channel) {
	return (key) => {
		if (key === "prices") {
			return !channel?.disablePrices;
		}
		if (key === "takedownDate") {
			return !channel?.preventTakedown;
		}
		return true;
	};
}
